import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import api from '../../../services/api'
import Icon from '../../../components/Icon'
import { Button, TableShell } from '../../../components/ui'
import { calculateIncomeReport, isArtel } from './incomeReportCalculations'
import {
  ReportErrorState,
  ReportLoadingState,
  ReportPageShell,
  formatMoney,
  formatPercentValue,
} from './IncomeReportComponents'
import {
  buildCsvContent,
  formatCsvMoney,
  formatCsvPercent,
} from './incomeReportCsv'
import './Reports.css'

function downloadCsv(filename, rows) {
  const content = buildCsvContent(rows)
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

function LegacyIncomeReportAccountantPage() {
  const { id } = useParams()
  const [payload, setPayload] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const report = payload?.report
  const fetchReport = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await api.get(`/company/reports/${id}/income`)
      setPayload(response.data || {})
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось загрузить отчет')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchReport()
  }, [id])

  const result = useMemo(() => calculateIncomeReport({
    sourceRows: payload?.source_rows || [],
    config: payload?.config || {},
    overrides: payload?.overrides || [],
  }), [payload])

  const rows = useMemo(() => result.rows
    .filter((row) => isArtel(row.employee_type))
    .sort((a, b) => b.sharePercent - a.sharePercent || String(a.employee_name).localeCompare(String(b.employee_name), 'ru')), [result.rows])

  const totals = useMemo(() => rows.reduce((acc, row) => ({
    sharePercent: acc.sharePercent + (row.sharePercent || 0),
    otherParticipation: acc.otherParticipation + (row.otherParticipation || 0),
    laborPay: acc.laborPay + (row.laborPay || 0),
    sharePayment: acc.sharePayment + (row.sharePayment || 0),
    otherPayment: acc.otherPayment + (row.otherPayment || 0),
    totalBeforeTax: acc.totalBeforeTax + (row.totalBeforeTax || 0),
  }), {
    sharePercent: 0,
    otherParticipation: 0,
    laborPay: 0,
    sharePayment: 0,
    otherPayment: 0,
    totalBeforeTax: 0,
  }), [rows])

  const exportCsv = () => {
    const csvRows = [
      ['№', 'ФИО Сотрудника', '% пая', '% иного участия', 'Выплата по паевому', 'Выплата по трудовому', 'Выплата по иному', 'Общая доля (без НДФЛ)'],
      ...rows.map((row, index) => [
        index + 1,
        row.employee_name,
        formatCsvPercent(row.sharePercent),
        formatCsvPercent(row.otherParticipation, { fraction: true }),
        formatCsvMoney(row.sharePayment),
        formatCsvMoney(row.laborPay),
        formatCsvMoney(row.otherPayment),
        formatCsvMoney(row.totalBeforeTax),
      ]),
      [
        '',
        'ИТОГО',
        formatCsvPercent(totals.sharePercent),
        formatCsvPercent(totals.otherParticipation, { fraction: true }),
        formatCsvMoney(totals.sharePayment),
        formatCsvMoney(totals.laborPay),
        formatCsvMoney(totals.otherPayment),
        formatCsvMoney(totals.totalBeforeTax),
      ],
    ]
    downloadCsv(`income-accountant-${report?.period_label || id}.csv`, csvRows)
  }

  if (loading) return <ReportLoadingState message="Загрузка бухгалтерского отчета…" />
  if (error && !payload) return <ReportErrorState message={error} onRetry={fetchReport} />

  const legacy = (
    <ReportPageShell>
      <div className="report-toolbar report-toolbar--compact no-print">
        <div className="report-toolbar__meta">
          <h1>Бухгалтерский отчет</h1>
          <span>{report?.period_label || '—'}</span>
        </div>
        <div className="report-toolbar__actions">
          <Button variant="secondary" onClick={exportCsv}><Icon name="download" size={16} /> CSV</Button>
          <Button variant="primary" onClick={() => window.print()}><Icon name="download" size={16} /> PDF</Button>
        </div>
      </div>

      {error && <div className="message message-error reports-message">{error}</div>}

      <section className="report-print-page">
        <div className="report-print-header">
          <div>
            <h2>Квартальный отчет по доходам</h2>
            <p>Для бухгалтерии · {report?.period_label || '—'}</p>
          </div>
          <div className="report-print-date">{new Date().toLocaleDateString('ru-RU')}</div>
        </div>
        <TableShell className="report-config-table-shell">
          <table className="report-config-table report-accountant-table">
            <thead>
              <tr>
                <th>№</th>
                <th>ФИО Сотрудника</th>
                <th className="report-num">% пая</th>
                <th className="report-num">% иного участия</th>
                <th className="report-num">Выплата по паевому</th>
                <th className="report-num">Выплата по трудовому</th>
                <th className="report-num">Выплата по иному</th>
                <th className="report-num">Общая доля (₽ без НДФЛ)</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="report-empty-cell">Нет артельщиков в отчете</td></tr>}
              {rows.map((row, index) => (
                <tr key={row.row_key}>
                  <td>{index + 1}</td>
                  <td>{row.employee_name}</td>
                  <td className="report-num">{formatPercentValue(row.sharePercent)}</td>
                  <td className="report-num">{formatPercentValue(row.otherParticipation, { fraction: true })}</td>
                  <td className="report-num">{formatMoney(row.sharePayment)}</td>
                  <td className="report-num">{formatMoney(row.laborPay)}</td>
                  <td className="report-num">{formatMoney(row.otherPayment)}</td>
                  <td className="report-num">{formatMoney(row.totalBeforeTax)}</td>
                </tr>
              ))}
              {rows.length > 0 && (
                <tr className="report-total-row">
                  <td />
                  <td><strong>ИТОГО</strong></td>
                  <td className="report-num">{formatPercentValue(totals.sharePercent)}</td>
                  <td className="report-num">{formatPercentValue(totals.otherParticipation, { fraction: true })}</td>
                  <td className="report-num">{formatMoney(totals.sharePayment)}</td>
                  <td className="report-num">{formatMoney(totals.laborPay)}</td>
                  <td className="report-num">{formatMoney(totals.otherPayment)}</td>
                  <td className="report-num">{formatMoney(totals.totalBeforeTax)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </TableShell>
      </section>
    </ReportPageShell>
  )
  return legacy
}

export default LegacyIncomeReportAccountantPage
